/**
 * VersionCard Component (V2)
 *
 * Zeigt eine einzelne Version mit Aktionen.
 * WCAG 2.1 Level AA + BFSG konform
 */

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownUp, X, RotateCcw, Calendar, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, Badge } from '@/components/v2/ui'
import type { ContentBlockVersion } from '@/types/versioning'
import type { PublishingSchedule } from '@/types/publishing'
import { VersionBadgeCompact } from './VersionBadge'
import { formatRelativeTime, formatScheduleDate } from '@/hooks/usePublishSchedule'

type VersionVariant = 'current' | 'scheduled' | 'history'

interface VersionCardProps {
  version: ContentBlockVersion
  variant: VersionVariant
  schedule?: PublishingSchedule | null
  onCompare?: () => void
  onRestore?: () => void
  onSchedule?: () => void
  onCancelSchedule?: () => void
  isCompareMode?: boolean
  isSelected?: boolean
  onSelect?: () => void
}

export function VersionCard({
  version,
  variant,
  schedule,
  onCompare,
  onRestore,
  onSchedule,
  onCancelSchedule,
  isCompareMode,
  isSelected,
  onSelect,
}: VersionCardProps) {
  const { t } = useTranslation()
  const formattedDate = formatRelativeTime(version.created_at)

  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-colors',
        'bg-white',
        'border-neutral-200',
        variant === 'current' && 'border-l-4 border-l-primary-500',
        variant === 'scheduled' && 'border-l-4 border-l-amber-500',
        isSelected && 'ring-2 ring-primary-500 bg-primary-50',
        isCompareMode && 'cursor-pointer hover:bg-neutral-50'
      )}
      onClick={isCompareMode ? onSelect : undefined}
      role={isCompareMode ? 'button' : undefined}
      tabIndex={isCompareMode ? 0 : undefined}
      onKeyDown={isCompareMode ? (e) => e.key === 'Enter' && onSelect?.() : undefined}
    >
      <div className="flex items-start justify-between">
        {/* Version Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <VersionBadgeCompact
              status={version.status}
              isCurrent={variant === 'current'}
              isScheduled={variant === 'scheduled'}
            />
            <strong className="text-neutral-900">
              V{version.version_number}
            </strong>
            {version.diff_summary && (
              <span className="text-neutral-500 text-sm truncate">
                "{version.diff_summary}"
              </span>
            )}
          </div>

          <div className="text-sm text-neutral-500">
            {version.changed_by_name || 'System'} • {formattedDate}
          </div>

          {/* Schedule Info */}
          {variant === 'scheduled' && schedule && (
            <div className="mt-2 text-sm">
              <span className="text-amber-600 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatScheduleDate(schedule.publish_start_at)} – {formatScheduleDate(schedule.publish_end_at)}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isCompareMode && (
          <div className="flex gap-1 ml-2">
            {variant === 'current' && onCompare && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCompare}
                title={t('common.compare')}
                aria-label="Mit anderer Version vergleichen"
              >
                <ArrowDownUp className="h-4 w-4" />
              </Button>
            )}

            {variant === 'scheduled' && onCancelSchedule && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelSchedule}
                title="Zeitplan abbrechen"
                aria-label="Zeitplan abbrechen"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            )}

            {variant === 'history' && (
              <>
                {onCompare && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onCompare}
                    title={t('common.compare')}
                    aria-label="Mit aktueller Version vergleichen"
                  >
                    <ArrowDownUp className="h-4 w-4" />
                  </Button>
                )}
                {onRestore && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRestore}
                    title={t('common.restore')}
                    aria-label="Version wiederherstellen"
                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                {onSchedule && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSchedule}
                    title="Planen"
                    aria-label="Version planen"
                    className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Compare Mode Selection */}
        {isCompareMode && (
          <div className="ml-2">
            <input
              type="radio"
              className={cn(
                'h-4 w-4',
                'text-primary-500 border-neutral-300',
                'focus:ring-primary-500 focus:ring-offset-0',
                // BFSG: Touch target
                'min-h-[44px] min-w-[44px] p-2'
              )}
              checked={isSelected}
              onChange={onSelect}
              aria-label={t('publishing.selectVersionAria', { number: version.version_number })}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Section Header for version groups
 */
interface VersionSectionProps {
  title: string
  icon?: ReactNode
  count?: number
  variant?: 'primary' | 'warning' | 'info' | 'default'
}

export function VersionSection({
  title,
  icon,
  count,
  variant = 'default',
}: VersionSectionProps) {
  const variantClasses = {
    primary: 'text-green-600',
    warning: 'text-amber-600',
    info: 'text-primary-600',
    default: 'text-neutral-500',
  }

  return (
    <div className={cn('flex items-center gap-2 mb-2', variantClasses[variant])}>
      {icon}
      <span className="uppercase text-xs font-bold tracking-wide">{title}</span>
      {count !== undefined && count > 0 && (
        <Badge variant="secondary" className="text-xs">
          {count}
        </Badge>
      )}
    </div>
  )
}
